/**
 * Comment Section Component
 * TASK-213: Build Transaction Detail Modal
 *
 * Allows adding compliance comments/notes to transactions
 * Mock implementation - will integrate with backend in future
 */

import React, { useState } from 'react';
import { MessageSquare, Send } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { formatDateTime } from '../../utils/formatters';
import { toast } from 'sonner';

interface CommentSectionProps {
  /** Comment submit handler */
  onAddComment: (comment: string) => void;
}

interface MockComment {
  id: string;
  author: string;
  text: string;
  timestamp: string;
}

const CommentSection: React.FC<CommentSectionProps> = ({ onAddComment }) => {
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mock existing comments (in real app, these would come from props/API)
  const [mockComments] = useState<MockComment[]>([]);

  // Character limit
  const maxLength = 500;
  const remainingChars = maxLength - comment.length;

  // Handle submit
  const handleSubmit = async () => {
    if (!comment.trim()) return;
    if (comment.length > maxLength) return;

    setIsSubmitting(true);

    try {
      // Call parent handler
      onAddComment(comment.trim());

      // Show notification
      toast.info(
        'Comment added. Note: Comments are not persisted in this prototype.'
      );

      // Clear input
      setComment('');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Enter key (Shift+Enter for new line)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Card className="border border-gray-200">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Compliance Comments</h3>
        </div>

        {/* Existing Comments */}
        {mockComments.length > 0 && (
          <div className="space-y-3 mb-4">
            {mockComments.map((mockComment) => (
              <div
                key={mockComment.id}
                className="p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900">
                    {mockComment.author}
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatDateTime(mockComment.timestamp)}
                  </span>
                </div>
                <p className="text-sm text-gray-700">{mockComment.text}</p>
              </div>
            ))}
          </div>
        )}

        {mockComments.length === 0 && (
          <p className="text-sm text-gray-500 mb-4">
            No comments yet. Add a compliance note below.
          </p>
        )}

        {/* Add Comment Form */}
        <div className="space-y-2">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add a compliance note... (Press Enter to submit, Shift+Enter for new line)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            rows={3}
            maxLength={maxLength}
            disabled={isSubmitting}
          />

          {/* Character Count & Submit */}
          <div className="flex items-center justify-between">
            <span
              className={`text-xs ${
                remainingChars < 50 ? 'text-orange-600' : 'text-gray-500'
              }`}
            >
              {remainingChars} characters remaining
            </span>
            <button
              onClick={handleSubmit}
              disabled={!comment.trim() || isSubmitting || comment.length > maxLength}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Add Comment
                </>
              )}
            </button>
          </div>
        </div>

        {/* Info Note */}
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800">
            <span className="font-medium">Note:</span> Comments are logged but not
            persisted in this prototype. Backend integration pending.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default CommentSection;
